export interface MidInspectionItem {
  itemId?: number;
  sortOrder?: number | null;
  processName: string;
  itemName: string;
  standardText?: string | null;
  methodText?: string | null;
  sample1?: string | null;
  sample2?: string | null;
  sample3?: string | null;
  sample4?: string | null;
  sample5?: string | null;
  judgment?: string | null;
}

/** 중간검사 투입 원자재 (재고 품목 기준, Lot/인수검사는 선택) */
export interface MidInspectionInput {
  inputId?: number;
  sortOrder?: number | null;
  acceptanceInspectionId?: number | null;
  inventoryId: number;
  itemKind?: string | null;
  itemName?: string | null;
  specText?: string | null;
  lotNo?: string | null;
  qty?: string | null;
  stockQty?: number | string | null;
}

/** 투입 선택용 승인 인수검사 목록 */
export interface MidInputSource {
  acceptanceInspectionId: number;
  inventoryId: number;
  itemKind?: string | null;
  itemName?: string | null;
  specText?: string | null;
  lotNo?: string | null;
  incomingQty?: string | null;
  stockQty?: number | string | null;
  inspectionDate?: string | null;
  overallJudgment?: string | null;
}

export interface MidInspection {
  inspectionId: number;
  inventoryId: number;
  contractId?: number | null;
  itemKind?: string | null;
  itemName?: string | null;
  specText?: string | null;
  formId?: number | null;
  formTitle?: string | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  canRevoke?: boolean | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  status: string;
  lotNo?: string | null;
  lotQty?: string | null;
  inspectorName?: string | null;
  inspectionDate?: string | null;
  categoryLabel?: string | null;
  appearanceRemark?: string | null;
  appearanceJudgment?: string | null;
  electricalRemark?: string | null;
  electricalJudgment?: string | null;
  agingRemark?: string | null;
  agingJudgment?: string | null;
  overallJudgment?: string | null;
  remark?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  items?: MidInspectionItem[] | null;
  inputs?: MidInspectionInput[] | null;
  attachments?: MidInspectionAttachment[] | null;
}

export interface MidInspectionAttachment {
  attachmentId: number;
  attachmentKind?: string | null;
  fileName: string;
  contentType?: string | null;
  fileSize?: number | null;
  createdAt?: string | null;
}

export interface MidInspectionPayload {
  inventoryId: number;
  contractId?: number | null;
  formId?: number | null;
  formTitle?: string;
  authorName?: string;
  status?: string;
  lotNo?: string;
  lotQty?: string;
  inspectorName?: string;
  inspectionDate?: string | null;
  categoryLabel?: string;
  appearanceRemark?: string;
  appearanceJudgment?: string;
  electricalRemark?: string;
  electricalJudgment?: string;
  agingRemark?: string;
  agingJudgment?: string;
  overallJudgment?: string;
  remark?: string;
  items?: MidInspectionItem[];
  inputs?: MidInspectionInput[];
}

export const EMPTY_MID_ITEM: MidInspectionItem = {
  processName: '',
  itemName: '',
  standardText: '',
  methodText: '',
  sample1: '',
  sample2: '',
  sample3: '',
  sample4: '',
  sample5: '',
  judgment: '',
};

export function midInspectionItemLabel(row: Pick<MidInspection, 'itemName'>) {
  return row.itemName?.trim() || '-';
}

export function midInspectionStatusLabel(
  status?: string | null,
  approvalInProgress?: boolean | null
) {
  if (approvalInProgress) return '결재 요청 중';
  if (status === 'APPROVED') return '승인완료';
  if (status === 'COMPLETED') return '등록완료';
  if (status === 'DRAFT') return '작성중';
  return status || '-';
}

export function midInputSourceLabel(row: MidInputSource) {
  const name = row.itemName?.trim() || '품명 없음';
  const spec = row.specText?.trim() ? ` · ${row.specText.trim()}` : '';
  const lot = row.lotNo?.trim() ? ` / Lot ${row.lotNo.trim()}` : '';
  const qty = row.incomingQty?.trim() ? ` (${row.incomingQty.trim()})` : '';
  const stock =
    row.stockQty != null && row.stockQty !== ''
      ? ` · 재고 ${String(row.stockQty)}`
      : '';
  return `${name}${spec}${lot}${qty}${stock}`;
}

/** 투입 수량 문자열 → 숫자 (검증용). 실패 시 null */
export function parseMidQty(raw?: string | null): number | null {
  if (raw == null || !String(raw).trim()) return null;
  const cleaned = String(raw)
    .trim()
    .replace(/,/g, '')
    .replace(/[^0-9.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

/** 동일 inventoryId 투입 합계가 보유 재고를 넘으면 메시지 반환 */
export function validateMidInputStock(
  inputs: MidInspectionInput[],
  stockByInventoryId: Map<number, number>
): string | null {
  const requested = new Map<number, number>();
  for (const input of inputs) {
    if (input.inventoryId == null || input.inventoryId <= 0) {
      return '투입 품목을 선택해 주세요.';
    }
    const qty = parseMidQty(input.qty);
    if (qty == null || qty <= 0) {
      return `"${input.itemName || '투입 품목'}" 투입 수량을 올바르게 입력해 주세요.`;
    }
    requested.set(input.inventoryId, (requested.get(input.inventoryId) ?? 0) + qty);
  }
  for (const [inventoryId, total] of Array.from(requested.entries())) {
    const stock = stockByInventoryId.get(inventoryId) ?? 0;
    const name =
      inputs.find((row) => row.inventoryId === inventoryId)?.itemName?.trim() || '품목';
    if (total > stock) {
      return `"${name}" 투입 합계(${total})가 보유 재고(${stock})를 초과합니다.`;
    }
  }
  return null;
}
